import React, { useState } from 'react';
import {
  Alert, Button, IconButton, Paper, Stack, TextField, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useOperations } from './OperationsProvider';

// The shifts a plant runs, in its own names.
//
// Everybody at a plant refers to a shift by name - A, B, C, General, Night - and the shift report
// has to arrive saying which one it covers, not two times somebody has to decode. The start times
// are what divide the day: each shift runs until the next one starts, and the last runs round into
// the first, which is what makes a night shift a single period rather than two halves either side of
// midnight.
const DEFAULTS = [{ name: 'A', start: '06:00' }, { name: 'B', start: '14:00' }, { name: 'C', start: '22:00' }];

const ShiftRules = () => {
  const { data, mutate } = useOperations();
  const stored = (data.config?.shifts || []);
  const [shifts, setShifts] = useState(stored.length ? stored : DEFAULTS);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(false);
  const set = (index, key, value) => setShifts((current) => current.map((shift, i) => (i === index ? { ...shift, [key]: value } : shift)));

  const save = async () => {
    setPending(true); setError(''); setNotice('');
    try {
      await mutate('config', { ...data.config, shifts: shifts.filter((shift) => shift.name && shift.start) });
      setNotice('Shifts saved. Shift reports now follow these times.');
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  // Where each shift ends: at the next one, and the last at the first of the next day.
  const ordered = [...shifts].filter((shift) => shift.start).sort((a, b) => a.start.localeCompare(b.start));
  const covers = (shift) => {
    const index = ordered.findIndex((other) => other.start === shift.start);
    if (index < 0) return '';
    const next = ordered[(index + 1) % ordered.length];
    return ordered.length < 2 ? 'the whole day' : `${shift.start} to ${next.start}${index === ordered.length - 1 ? ' the next day' : ''}`;
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack gap={2}>
        {/* The section heading above already says Shifts; repeating it here would be the page
            talking to itself. */}
        <Typography variant="body2" color="text.secondary">Each shift runs until the next one starts; the last runs through the night into the first. A shift report is sent at each changeover, covering the shift that has just ended.</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
        {shifts.map((shift, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Stack key={index} direction="row" gap={2} alignItems="center">
            <TextField
              label="Shift name"
              value={shift.name || ''}
              disabled={pending}
              onChange={(e) => set(index, 'name', e.target.value)}
              inputProps={{ maxLength: 40 }}
              sx={{ width: 200 }}
            />
            <TextField
              label="Starts at"
              type="time"
              value={shift.start || ''}
              disabled={pending}
              onChange={(e) => set(index, 'start', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              sx={{ width: 160 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{covers(shift) && `Covers ${covers(shift)}`}</Typography>
            <IconButton aria-label={`Remove ${shift.name || 'this shift'}`} disabled={pending || shifts.length <= 1} onClick={() => setShifts((current) => current.filter((unused, i) => i !== index))}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}
        <Stack direction="row" gap={2}>
          <Button disabled={pending || shifts.length >= 6} onClick={() => setShifts((current) => [...current, { name: '', start: '' }])}>Add a shift</Button>
          <Button variant="contained" disabled={pending} onClick={save}>{pending ? 'Saving…' : 'Save shifts'}</Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ShiftRules;
